/**
 * 公共表单校验工具
 * 兼容 Element Plus el-form rules 格式
 *
 * 使用方式：
 *   import { required, isPhone, isEmail, ... } from '@/utils/validators'
 *
 *   const rules = {
 *     name:    [required('请输入名称')],
 *     phone:   [required('请输入联系电话'), isPhone()],
 *     email:   [isEmail()],
 *     code:    [isAlphanumeric()],
 *   }
 *
 * 也可单独调用校验函数做手动判断：
 *   validators.isPhoneValue('13800138000') // true
 */

// ======================== 正则表达式 ========================

const REGEX = {
  // 手机号（中国大陆）：1开头，第二位3-9，共11位
  mobile: /^1[3-9]\d{9}$/,
  // 座机号：区号(3-4位) + 号码(7-8位)，可带分机号
  landline: /^(0\d{2,3})-?(\d{7,8})(-\d{1,6})?$/,
  // 400热线：400开头，后跟7位数字，可带连字符
  hotline400: /^400-?\d{3}-?\d{4}$/,
  // 手机号或座机或400热线
  phone: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?|400-?\d{3}-?\d{4})$/,
  // 邮箱
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  // 纯数字
  number: /^\d+$/,
  // 数字（含小数）
  price: /^\d+(\.\d+)?$/,
  // 数字或字母
  alphanumeric: /^[a-zA-Z0-9]+$/,
  // 桩ID格式：数字、字母和-符号组合，如 00-01-123456 或 A0-01-ABC
  pileId: /^[a-zA-Z0-9\-]+$/,
  // 网址格式（支持含协议）：http/https协议 + 域名或IP，可带端口和路径，如 https://example.com、http://192.168.1.1/path
  url: /^(https?:\/\/)?((([a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})|((\d{1,3}\.){3}\d{1,3})|(localhost))(:[0-9]+)?(\/[^\s]*)?$/,
  // 中文
  chinese: /^[\u4e00-\u9fa5]+$/,
  // 特殊字符（含空格）
  specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?\s~`]+/,
  // 不含特殊字符（允许中文、字母、数字、常见标点）
  noSpecialChar: /^[a-zA-Z0-9\u4e00-\u9fa5]+$/,
  // 强密码：开头必须是大写字母，至少8个字符，包含大写字母、小写字母、数字和至少一个特殊字符
  strongPassword: /^[A-Z](?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{7,}$/,
  // 统一社会信用代码：18位，由数字和大写字母组成
  creditCode: /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
};

// ======================== Element Plus 规则工厂 ========================

/**
 * 必填校验
 * @param {string} msg 提示信息
 * @param {string} [trigger='blur'] 触发时机
 */
export function required(msg = '不能为空', trigger = 'blur') {
  return {
    required: true,
    message: msg,
    trigger,
  };
}

/**
 * 手机号或座机号校验
 * @param {string} [msg] 提示信息
 * @param {string} [trigger='blur']
 */
export function isPhone(msg = '请输入正确的手机号或座机号', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.phone.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 仅手机号校验
 */
export function isMobile(msg = '请输入正确的手机号', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.mobile.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 邮箱校验
 */
export function isEmail(msg = '请输入正确的邮箱格式', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.email.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 只能填数字
 */
export function isNumber(msg = '只能输入数字', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.number.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 只能填数字（含小数）
 */
export function isPrice(msg = '只能输入数字', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value && value !== 0) return callback();
      if (REGEX.price.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 数字必须大于指定值（含小数）
 * @param {number} min 最小值（不包含）
 * @param {string} [msg] 提示信息
 * @param {string} [trigger='blur']
 */
export function isPriceGreaterThan(min, msg, trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value && value !== 0) return callback();
      const numValue = Number(String(value).trim());
      if (isNaN(numValue)) {
        callback(new Error(msg || '只能输入数字'));
      } else if (numValue <= min) {
        callback(new Error(msg || `必须大于${min}`));
      } else {
        callback();
      }
    },
    trigger,
  };
}

/**
 * 非负数校验（必须 >= 0）
 * @param {string} [msg] 提示信息
 * @param {string} [trigger='blur']
 */
export function isNonNegative(msg = '不能为负数', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value && value !== 0) return callback();
      const numValue = Number(String(value).trim());
      if (isNaN(numValue)) {
        callback(new Error('只能输入数字'));
      } else if (numValue < 0) {
        callback(new Error(msg));
      } else {
        callback();
      }
    },
    trigger,
  };
}

/**
 * 只能输入数字或字母
 */
export function isAlphanumeric(msg = '只能输入数字或字母', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.alphanumeric.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 桩ID格式校验：只能输入数字、字母和-符号
 */
export function isPileId(msg = '只能输入数字、字母和-符号', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.pileId.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 网址格式校验（支持含协议）：http/https协议 + 域名或IP，可带端口和路径
 */
export function isUrl(msg = '请输入正确的网址格式（如：https://example.com）', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.url.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 只能输入中文
 */
export function isChinese(msg = '只能输入中文', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.chinese.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 不能输入特殊字符
 */
export function noSpecialChar(msg = '不能输入特殊字符', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (!REGEX.specialChar.test(String(value))) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 强密码校验：开头必须是大写字母，至少8个字符，包含大写字母、小写字母、数字和至少一个特殊字符
 */
export function isStrongPassword(msg = '密码开头必须是大写字母，至少8个字符，包含大写字母、小写字母、数字和至少一个特殊字符', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.strongPassword.test(String(value))) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 统一社会信用代码校验：18位，由数字和大写字母组成
 */
export function isCreditCode(msg = '请输入正确的统一社会信用代码（18位）', trigger = 'blur') {
  return {
    validator: (rule, value, callback) => {
      if (!value) return callback();
      if (REGEX.creditCode.test(String(value).trim())) {
        callback();
      } else {
        callback(new Error(msg));
      }
    },
    trigger,
  };
}

/**
 * 最小长度校验
 */
export function minLength(len, msg, trigger = 'blur') {
  return {
    min: len,
    message: msg || `最少输入${len}个字符`,
    trigger,
  };
}

/**
 * 最大长度校验
 */
export function maxLength(len, msg, trigger = 'blur') {
  return {
    max: len,
    message: msg || `最多输入${len}个字符`,
    trigger,
  };
}

// ======================== 独立校验函数（非 Element Plus 规则） ========================

/** 校验手机号 */
export function isMobileValue(val) {
  return REGEX.mobile.test(String(val || '').trim());
}

/** 校验座机号 */
export function isLandlineValue(val) {
  return REGEX.landline.test(String(val || '').trim());
}

/** 校验手机号或座机号 */
export function isPhoneValue(val) {
  return REGEX.phone.test(String(val || '').trim());
}

/** 校验邮箱 */
export function isEmailValue(val) {
  return REGEX.email.test(String(val || '').trim());
}

/** 校验纯数字 */
export function isNumberValue(val) {
  return REGEX.number.test(String(val || '').trim());
}

/** 校验数字（含小数） */
export function isPriceValue(val) {
  return REGEX.price.test(String(val || '').trim());
}

/** 校验数字或字母 */
export function isAlphanumericValue(val) {
  return REGEX.alphanumeric.test(String(val || '').trim());
}

/** 校验桩ID格式（数字和-符号） */
export function isPileIdValue(val) {
  return REGEX.pileId.test(String(val || '').trim());
}

/** 校验网址格式（不含协议） */
export function isUrlValue(val) {
  return REGEX.url.test(String(val || '').trim());
}

/** 校验纯中文 */
export function isChineseValue(val) {
  return REGEX.chinese.test(String(val || '').trim());
}

/** 校验是否包含特殊字符 */
export function hasSpecialChar(val) {
  return REGEX.specialChar.test(String(val || ''));
}

/** 校验强密码格式 */
export function isStrongPasswordValue(val) {
  return REGEX.strongPassword.test(String(val || ''));
}

// 默认导出汇总，方便 import * as validators
export default {
  required,
  isPhone,
  isMobile,
  isEmail,
  isNumber,
  isPrice,
  isPriceGreaterThan,
  isNonNegative,
  isAlphanumeric,
  isPileId,
  isUrl,
  isChinese,
  noSpecialChar,
  isStrongPassword,
  minLength,
  maxLength,
  isMobileValue,
  isLandlineValue,
  isPhoneValue,
  isEmailValue,
  isNumberValue,
  isPriceValue,
  isAlphanumericValue,
  isPileIdValue,
  isUrlValue,
  isChineseValue,
  hasSpecialChar,
  isStrongPasswordValue,
  REGEX,
};